import api from '../api';

export interface ProcessStep {
  id: number;
  name: string;
  processStepName?: string;
  description?: string;
  department?: string;
  owner?: string;
}

export interface ComplianceSection {
  applicable?: boolean;
  requirements: string[];
  risk_level: 'low' | 'medium' | 'high';
  notes: string;
  frameworks?: string[];
  audit_trail_needed?: boolean;
  retention_period?: string;
  pii_involved?: boolean;
  data_classification?: string;
  sod_required?: boolean;
  approval_required?: boolean;
  approval_levels?: string[];
  required_docs?: string[];
}

// Enhanced Compliance Interfaces (New 7-Section Format)
export interface SOXControl {
  controlId: string;
  controlName: string;
  category: string;
  description: string;
  whyImpacted: string;
  objectives: string[];
  relevanceScore: number;
}

export interface ControlActivity {
  id: string;
  activity: string;
  controlReference: string;
  category: string;
  frequency: string;
  responsible: string;
  status: 'pending' | 'completed';
  evidenceRequired: string;
}

export interface EnhancedComplianceRequirements {
  sox_controls?: {
    applicable: boolean;
    controls: SOXControl[];
    controlCount: number;
    itGeneralControlsCount: number;
    businessProcessControlsCount: number;
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  control_activities?: {
    applicable: boolean;
    checklist: ControlActivity[];
    totalActivities: number;
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  change_management?: {
    applicable: boolean;
    changeType: string;
    approvalLevels: string[];
    sopReferences: { code: string; name: string; section: string }[];
    trainingRequirements: {
      requiredRoles: string[];
      estimatedUsers: string;
      trainingDuration: string;
      trainingType: string;
    };
    testingPeriod: {
      duration: string;
      phases: { name: string; duration: string }[];
      parallelRunRequired: boolean;
      regressionRequired: boolean;
    };
    notificationRequirements: string[];
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  data_governance?: {
    applicable: boolean;
    dataClassification: string;
    classificationLevel: { name: string; description: string; color: string };
    dataTypesInvolved: { type: string; name: string; category: string }[];
    piiInvolved: boolean;
    financialDataInvolved: boolean;
    privacyRequirements: string[];
    retentionRequirements: { period: string; details: string };
    dataResidency: { considerations: string[]; requiresReview: boolean };
    applicableFrameworks: string[];
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  validation_monitoring?: {
    applicable: boolean;
    preImplementation: {
      tests: { type: string; description: string; threshold: string }[];
      accuracyThreshold: string;
      signOffRequired: boolean;
    };
    pilotPhase: {
      required: boolean;
      duration: string;
      parallelRunRequired: boolean;
      rollbackTestRequired: boolean;
    };
    ongoingMonitoring: {
      kpis: { name: string; description: string; threshold: string; frequency: string; alertThreshold: string }[];
      qualitySampling: { rate: string; frequency: string; reviewedBy: string };
      reviewFrequency: string;
    };
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  risk_mitigation?: {
    applicable: boolean;
    failureScenarios: {
      scenario: string;
      description: string;
      impact: string;
      businessImpact: string;
      probability: string;
      mitigationControls: string[];
      escalationPath: string;
      recoveryTime: string;
      rollbackProcedure: string;
    }[];
    escalationProcedures: {
      levels: { level: number; title: string; authority: string }[];
      timeframes: Record<string, string>;
    };
    rollbackPlan: {
      required: boolean;
      components: string[];
      maxRollbackTime: string;
      approvalRequired: boolean;
    };
    decisionAuthority: { level: number; title: string; authority: string; examples: string[] }[];
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  regulatory_exam_prep?: {
    applicable: boolean;
    auditorQuestions: { category: string; question: string; suggestedResponse: string }[];
    evidenceRequirements: {
      categories: string[];
      items: Record<string, string[]>;
      totalItems: number;
    };
    documentationStandards: {
      format: string[];
      content: string[];
      retention: string[];
    };
    auditReadiness: {
      preparationChecklist: string[];
      documentInventory: { document: string; location: string }[];
      reviewSchedule: string;
    };
    riskLevel: 'low' | 'medium' | 'high';
    notes: string;
    requirements: string[];
  };
  metadata?: {
    generatedAt: string;
    opportunityId: number;
    automationType: string;
    complexity: string;
    overallRiskLevel: 'low' | 'medium' | 'high';
  };
}

export interface OptimizationOpportunity {
  id: number;
  inefficiency_type: string;
  title: string;
  description: string;
  affected_steps: any[];
  pattern_details: any;
  solution_approach: string;
  implementation_complexity: 'low' | 'medium' | 'high';
  estimated_time_reduction: string;
  regulatory_considerations: string;
  primary_tools: string[];
  alternative_tools: string[];
  integration_requirements: Array<string | { system: string; access_type?: string }>;
  ai_integration_opportunities: string;
  ai_regulatory_notes: string;
  compliance_requirements?: EnhancedComplianceRequirements | {
    // Legacy format support
    sox_controls: ComplianceSection;
    regulatory_frameworks: ComplianceSection;
    audit_requirements: ComplianceSection;
    data_privacy: ComplianceSection;
    segregation_of_duties: ComplianceSection;
    change_management: ComplianceSection;
    documentation: ComplianceSection;
  };
  workflow_count: number;
  governance_complexity_score?: number;
  automation_readiness_score?: number;
  integration_complexity_score?: number;
  regulatory_risk_score?: number;
  recommended_platform?: string;
  recommended_approach?: string;
  integration_analysis?: {
    total_integrations: number;
    integrations: Array<{
      system: string;
      api_available: boolean;
      connector_available: boolean;
      auth_methods: string[];
      typical_approval_time: string;
      governance_complexity: string;
      recommendation: string;
    }>;
  };
  governance_analysis?: any;
  phased_rollout_plan?: {
    total_phases: number;
    recommended_starting_phase: number;
    phase_1?: any;
    phase_2?: any;
    phase_3?: any;
  };
  dependencies?: {
    prerequisites: Array<{
      id: number;
      dependency_type: string;
      dependency_strength: 'required' | 'recommended' | 'optional';
      reason: string;
      prerequisite_opportunity?: {
        id: number;
        title: string;
      };
    }>;
    blocks: Array<{
      id: number;
      dependency_type: string;
      dependency_strength: 'required' | 'recommended' | 'optional';
      reason: string;
      blocked_opportunity?: {
        id: number;
        title: string;
      };
    }>;
  };
  // Grounded Calculation Engine results
  data_source?: 'estimated' | 'grounded';
  annual_hours_saved?: number;
  weighted_hourly_rate?: number;
  annual_dollar_value?: number;
  error_reduction_value?: number;
  total_annual_benefit?: number;
  annual_savings?: number;
  total_implementation_cost?: number;
  npv_3year?: number;
  roi_3year_pct?: number;
  roi_percentage?: number;
  payback_months?: number;
  calculated_at?: string;
  grounded_date?: string | null;
  // Monte Carlo Sensitivity Analysis results
  mc_npv_p10?: number;
  mc_npv_p50?: number;
  mc_npv_p90?: number;
  mc_roi_p10?: number;
  mc_roi_p50?: number;
  mc_roi_p90?: number;
  mc_payback_p10?: number;
  mc_payback_p50?: number;
  mc_payback_p90?: number;
  mc_prob_positive_roi?: number;
  mc_prob_payback_12mo?: number;
  mc_histogram_data?: {
    npv: { bin_start: number; bin_end: number; count: number }[];
    roi: { bin_start: number; bin_end: number; count: number }[];
    payback: { bin_start: number; bin_end: number; count: number }[];
  };
  mc_n_simulations?: number;
  mc_calculated_at?: string;
  // Approval Status
  mrm_approval_status?: 'pending' | 'approved' | 'rejected';
  mrm_approval_date?: string;
  mrm_approved_by?: string;
  sod_approval_status?: 'pending' | 'approved' | 'rejected';
  sod_approval_date?: string;
  sod_approved_by?: string;
  manual_rank?: number | null;
  // Imported metrics data
  current_volume?: number | null;
  current_time_per_unit?: number | null;
  projected_time_per_unit?: number | null;
  time_savings_percentage?: number | null;
  implementation_cost?: number | null;
  annual_maintenance_cost?: number | null;
  // ROI calculations
  roi_data?: {
    annual_savings: number;
    total_implementation_cost: number;
    roi_percentage: number;
    payback_months: number | null;
    net_present_value: number | null;
  } | null;
  // Architecture and Data Flow
  architecture_diagram?: ArchitectureDiagram;
  data_flow_map?: DataFlowMap;
  integration_complexity?: IntegrationComplexity;
  // Stakeholder & Change Management
  stakeholder_analysis?: {
    stakeholders: Array<{
      id: string;
      name: string;
      department: string;
      role_type: string;
      responsible: boolean;
      accountable: boolean;
      consulted: boolean;
      informed: boolean;
      reasoning: string;
      approval_required: boolean;
      estimated_effort_hours?: number;
    }>;
    approval_chain: string[];
    bottleneck_risk: 'low' | 'medium' | 'high';
    total_approvers: number;
    cross_department_count: number;
    estimated_approval_days: number;
  };
  change_impact_data?: {
    roles_affected: number;
    departments_affected: string[];
    training_level: 'Low' | 'Medium' | 'High';
    training_requirements: Array<{
      role: string;
      training_type: string;
      estimated_hours: number;
      delivery_method: string;
    }>;
    estimated_training_hours: number;
    communication_triggers: string[];
    rollout_recommendation: 'big_bang' | 'phased' | 'pilot';
    change_velocity: 'slow' | 'moderate' | 'fast';
    support_requirements: {
      tier1_volume_increase: string;
      documentation_updates: number;
      faq_items_needed: number;
    };
  };
  resistance_risk_flags?: Array<{
    id: string;
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    mitigation: string;
    affected_groups: string[];
  }>;
}

export interface ArchitectureDiagram {
  systems: Array<{
    id: string;
    name: string;
    type: 'source' | 'target' | 'middleware' | 'ui' | 'database' | 'api';
    category: string;
    description: string;
    position: { x: number; y: number };
  }>;
  connections: Array<{
    id: string;
    from: string;
    to: string;
    dataType: string;
    protocol: string;
    direction: 'unidirectional' | 'bidirectional';
    description: string;
  }>;
  integrationPoints: Array<{
    id: string;
    system: string;
    complexity: 'simple' | 'moderate' | 'complex';
    authMethod: string;
    rateLimit: string;
    errorHandling: string;
    constraints: string[];
  }>;
  securityBoundaries?: Array<{
    id: string;
    name: string;
    systems: string[];
    accessControl: string;
  }>;
}

export interface DataFlowMap {
  source: {
    system: string;
    description: string;
  };
  transformations: Array<{
    step: string;
    system: string;
    description: string;
  }>;
  storage: Array<{
    system: string;
    type: 'temporary' | 'permanent';
    retention: string;
  }>;
  accessControl: Array<{
    point: string;
    roles: string[];
    permissions: string;
  }>;
}

export interface IntegrationComplexity {
  totalScore: number;
  simpleCount: number;
  moderateCount: number;
  complexCount: number;
  integrations: Array<{
    id: string;
    system: string;
    complexity: 'simple' | 'moderate' | 'complex';
    authMethod: string;
    rateLimit: string;
    errorHandling: string;
    constraints: string[];
  }>;
}

export interface OptimizationAnalysis {
  analysis: {
    id: number;
    project_id: number;
    total_steps: number;
    inefficiency_count: number;
    automation_opportunities: number;
    patterns_detected: any;
    analysis_date: string;
  };
  opportunities: OptimizationOpportunity[];
}

export interface WorkflowBlueprint {
  id: number;
  workflow_name: string;
  workflow_type: string;
  description: string;
  trigger_config: any;
  workflow_steps: any[];
  conditions: any[];
  integrations: string[];
  prerequisites: string[];
  step_by_step_instructions: any[];
  troubleshooting_tips: any[];
  external_resources: any[];
}

export interface AIAssistantSession {
  sessionId: string;
  context: any;
}

export interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  images?: string[];
  timestamp: string;
}

/**
 * Process Optimization API Service
 */
class ProcessOptimizationAPI {
  /**
   * Run optimization analysis for a project
   */
  async analyzeProject(projectId: number, processSteps?: ProcessStep[], forceRegenerate = false) {
    const response = await api.post(`/api/process-optimization/${projectId}/analyze`, {
      processSteps,
      forceRegenerate
    }, {
      timeout: 300000 // 5 minutes timeout
    });
    return response.data;
  }

  /**
   * Get optimization analysis for a project
   */
  async getAnalysis(projectId: number): Promise<{ success: boolean; data?: OptimizationAnalysis; needsGeneration?: boolean }> {
    try {
      const response = await api.get(`/api/process-optimization/${projectId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          needsGeneration: true
        };
      }
      throw error;
    }
  }

  /**
   * Get workflow blueprints for an opportunity
   * @param opportunityId - ID of the opportunity
   * @param generateIfMissing - Whether to generate workflows if they don't exist (default: true)
   * @param useAI - Whether to use AI for detailed instructions (default: true)
   * @param forceRegenerate - Whether to delete existing and regenerate (default: false)
   */
  async getWorkflows(opportunityId: number, generateIfMissing = true, useAI = true, forceRegenerate = false): Promise<WorkflowBlueprint[]> {
    const response = await api.get(`/api/process-optimization/opportunity/${opportunityId}/workflows`, {
      params: {
        generateIfMissing: generateIfMissing ? 'true' : 'false',
        useAI: useAI ? 'true' : 'false',
        forceRegenerate: forceRegenerate ? 'true' : 'false'
      },
      timeout: useAI ? 600000 : 30000 // 10 minutes for AI, 30 seconds for templates
    });
    return response.data.data;
  }

  /**
   * Create AI assistant session
   */
  async createAssistantSession(opportunityId: number, workflowBlueprintId?: number): Promise<AIAssistantSession> {
    const response = await api.post('/api/process-optimization/assistant/session', {
      opportunityId,
      workflowBlueprintId
    });
    return response.data.data;
  }

  /**
   * Send message to AI assistant
   */
  async sendMessage(sessionId: string, message: string, images?: string[]): Promise<{ message: string; sessionId: string }> {
    const response = await api.post('/api/process-optimization/assistant/message', {
      sessionId,
      message,
      images
    }, {
      timeout: 300000 // 5 minutes timeout for AI responses
    });
    return response.data.data;
  }

  /**
   * Get assistant session details
   */
  async getSession(sessionId: string) {
    const response = await api.get(`/api/process-optimization/assistant/session/${sessionId}`);
    return response.data.data;
  }

  /**
   * Update session context
   */
  async updateContext(sessionId: string, contextUpdates: any) {
    const response = await api.patch(`/api/process-optimization/assistant/session/${sessionId}/context`, {
      contextUpdates
    });
    return response.data;
  }

  /**
   * Delete analysis
   */
  async deleteAnalysis(analysisId: number) {
    const response = await api.delete(`/api/process-optimization/${analysisId}`);
    return response.data;
  }

  /**
   * Download PowerPoint deliverable for a project
   */
  async downloadPptx(projectId: number, projectName?: string): Promise<void> {
    const response = await api.post(`/api/export/optimization/${projectId}/pptx`, {}, {
      responseType: 'blob',
      timeout: 60000
    });
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    a.download = `${projectName ? projectName.replace(/[^a-z0-9]/gi, '_') : 'Process_Optimization'}_${date}.pptx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const processOptimizationAPI = new ProcessOptimizationAPI();
